/**
 * useStockMovements - TanStack Query Hook
 *
 * Hook pour l'historique des mouvements de stock d'un produit
 *
 * Pattern: Component → Hook (TanStack Query) → Service (API)
 */

import { useQuery } from '@tanstack/react-query';
import { productsAPI } from '@/utils/productsAPI';

// ==================== QUERY KEYS ====================

export const stockMovementsKeys = {
  all: ['stock-movements'] as const,
  byProduct: (productId: string) => [...stockMovementsKeys.all, productId] as const,
  paginated: (productId: string, page: number, limit: number) =>
    [...stockMovementsKeys.byProduct(productId), 'page', page, 'limit', limit] as const,
};

// ==================== TYPES ====================

export type StockMovementType = 'IN' | 'OUT' | 'ADJUSTMENT' | 'INITIAL' | 'RETURN' | 'TRANSFER';

export interface StockMovement {
  id: string;
  type: StockMovementType;
  quantity: number;
  previousStock: number;
  newStock: number;
  reason?: string;
  referenceType?: string;
  referenceId?: string;
  createdAt: string;
  createdBy?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
}

export interface StockMovementsResponse {
  movements: StockMovement[];
  totalPages: number;
  total: number;
}

// ==================== QUERIES ====================

/**
 * Hook pour récupérer les mouvements de stock d'un produit
 * Supporte la pagination
 */
export function useStockMovements(
  productId: string,
  page: number = 1,
  limit: number = 10,
  options?: { enabled?: boolean }
) {
  return useQuery<StockMovementsResponse>({
    queryKey: stockMovementsKeys.paginated(productId, page, limit),
    queryFn: async () => {
      const result = await productsAPI.getStockMovements(productId, { page, limit });
      return {
        movements: result.movements,
        totalPages: result.totalPages,
        total: result.total,
      };
    },
    enabled: options?.enabled ?? true,
    staleTime: 30 * 1000, // 30 seconds - movements change frequently
  });
}
